import { useMemo, useState } from "react";
import { api } from "../lib/api.js";
import type { Chapter, Manuscript, Part, SceneSummary } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useContextMenu, type MenuItem } from "../components/ContextMenu.js";
import { formatNumber } from "../lib/format.js";

interface Props {
  projectId: string;
  manuscript: Manuscript;
  activeSceneId: string | null;
  onSelectScene: (sceneId: string) => void;
  onChanged: () => Promise<void>;
  onRequestDelete: (kind: "chapter" | "scene" | "part", id: string, name: string) => void;
}

type DragItem =
  | { kind: "scene"; id: string; chapterId: string }
  | { kind: "chapter"; id: string };

export function ManuscriptTree({
  projectId,
  manuscript,
  activeSceneId,
  onSelectScene,
  onChanged,
  onRequestDelete
}: Props) {
  const toast = useToast();
  const { open: openMenu, menu } = useContextMenu();
  // Chapters default to collapsed so the navigator opens as a clean chapter list;
  // the chapter holding the scene you're in stays open so you can see your place.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const activeChapterId = manuscript.scenes.find((s) => s.id === activeSceneId)?.chapterId;
    return new Set(manuscript.chapters.filter((c) => c.id !== activeChapterId).map((c) => c.id));
  });
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  const [drag, setDrag] = useState<DragItem | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);

  const scenesByChapter = useMemo(() => {
    const map = new Map<string, SceneSummary[]>();
    for (const scene of manuscript.scenes) {
      const list = map.get(scene.chapterId) ?? [];
      list.push(scene);
      map.set(scene.chapterId, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.position - b.position);
    return map;
  }, [manuscript.scenes]);

  const chaptersByPart = useMemo(() => {
    const map = new Map<string | null, Chapter[]>();
    for (const chapter of manuscript.chapters) {
      const key = chapter.partId;
      const list = map.get(key) ?? [];
      list.push(chapter);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.position - b.position);
    return map;
  }, [manuscript.chapters]);

  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const run = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      await onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : `${label} failed`);
    }
  };

  // ---- Mutations ----
  const addChapter = (partId: string | null) =>
    run("Add chapter", async () => {
      const { chapter } = await api.createChapter(projectId, `Chapter ${manuscript.chapters.length + 1}`, partId);
      const { scene } = await api.createScene(chapter.id, "Scene 1");
      onSelectScene(scene.id);
    });

  const addScene = (chapterId: string) =>
    run("Add scene", async () => {
      const count = scenesByChapter.get(chapterId)?.length ?? 0;
      const { scene } = await api.createScene(chapterId, `Scene ${count + 1}`);
      onSelectScene(scene.id);
    });

  const addPart = () =>
    run("Add part", async () => {
      await api.createPart(projectId, `Part ${(chaptersByPart.size ? manuscript.parts.length : 0) + 1}`);
    });

  const commitRename = async () => {
    if (!renaming) return;
    const { id, value } = renaming;
    setRenaming(null);
    const trimmed = value.trim();
    if (!trimmed) return;
    const chapter = manuscript.chapters.find((c) => c.id === id);
    const part = manuscript.parts.find((p) => p.id === id);
    const scene = manuscript.scenes.find((s) => s.id === id);
    await run("Rename", async () => {
      if (chapter) await api.updateChapter(id, { title: trimmed });
      else if (part) await api.renamePart(id, trimmed);
      else if (scene) await api.updateScene(id, { title: trimmed });
    });
  };

  // ---- Drag & drop ----
  const reorderScenesForDrop = async (draggedSceneId: string, fromChapterId: string, toChapterId: string, beforeSceneId: string | null) => {
    const source = (scenesByChapter.get(fromChapterId) ?? []).map((s) => s.id).filter((id) => id !== draggedSceneId);
    const destBase = fromChapterId === toChapterId ? source : (scenesByChapter.get(toChapterId) ?? []).map((s) => s.id).filter((id) => id !== draggedSceneId);
    const insertAt = beforeSceneId ? destBase.indexOf(beforeSceneId) : destBase.length;
    const dest = [...destBase];
    dest.splice(insertAt < 0 ? dest.length : insertAt, 0, draggedSceneId);

    const groups =
      fromChapterId === toChapterId
        ? [{ chapterId: toChapterId, sceneIds: dest }]
        : [
            { chapterId: fromChapterId, sceneIds: source },
            { chapterId: toChapterId, sceneIds: dest }
          ];
    await run("Reorder", () => api.reorderScenes(projectId, groups));
  };

  const reorderChaptersForDrop = async (draggedChapterId: string, beforeChapterId: string) => {
    const ordered = manuscript.chapters.map((c) => ({ id: c.id, partId: c.partId }));
    const targetPart = manuscript.chapters.find((c) => c.id === beforeChapterId)?.partId ?? null;
    const filtered = ordered.filter((c) => c.id !== draggedChapterId);
    const insertAt = filtered.findIndex((c) => c.id === beforeChapterId);
    filtered.splice(insertAt < 0 ? filtered.length : insertAt, 0, { id: draggedChapterId, partId: targetPart });
    await run("Reorder", () => api.reorderChapters(projectId, filtered));
  };

  const sceneMenu = (scene: SceneSummary): MenuItem[] => [
    { label: "Rename", icon: "edit", onClick: () => setRenaming({ id: scene.id, value: scene.title }) },
    { label: "Delete scene", icon: "trash", danger: true, onClick: () => onRequestDelete("scene", scene.id, scene.title) }
  ];

  const chapterMenu = (chapter: Chapter): MenuItem[] => [
    { label: "Add scene", icon: "plus", onClick: () => addScene(chapter.id) },
    { label: "Rename", icon: "edit", onClick: () => setRenaming({ id: chapter.id, value: chapter.title }) },
    ...(manuscript.parts.length > 0
      ? [{ label: "Move out of part", icon: "layers" as const, onClick: () => void run("Move", () => api.updateChapter(chapter.id, { partId: null })) }]
      : []),
    { label: "Delete chapter", icon: "trash", danger: true, onClick: () => onRequestDelete("chapter", chapter.id, chapter.title) }
  ];

  const partMenu = (part: Part): MenuItem[] => [
    { label: "Add chapter", icon: "plus", onClick: () => addChapter(part.id) },
    { label: "Rename", icon: "edit", onClick: () => setRenaming({ id: part.id, value: part.title }) },
    { label: "Delete part", icon: "trash", danger: true, onClick: () => onRequestDelete("part", part.id, part.title) }
  ];

  const renderScene = (scene: SceneSummary) => {
    const isActive = scene.id === activeSceneId;
    const status = sceneStatus(scene.status);
    return (
      <div
        key={scene.id}
        className={`tree-scene ${isActive ? "active" : ""} ${dropHint === `scene:${scene.id}` ? "drop-before" : ""}`}
        draggable
        onDragStart={(e) => {
          setDrag({ kind: "scene", id: scene.id, chapterId: scene.chapterId });
          e.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(e) => {
          if (drag?.kind === "scene") {
            e.preventDefault();
            setDropHint(`scene:${scene.id}`);
          }
        }}
        onDragLeave={() => setDropHint((h) => (h === `scene:${scene.id}` ? null : h))}
        onDrop={(e) => {
          e.preventDefault();
          setDropHint(null);
          if (drag?.kind === "scene" && drag.id !== scene.id) {
            void reorderScenesForDrop(drag.id, drag.chapterId, scene.chapterId, scene.id);
          }
          setDrag(null);
        }}
        onClick={() => onSelectScene(scene.id)}
        onContextMenu={(e) => openMenu(e, sceneMenu(scene))}
      >
        <div className="tree-scene-main">
          <Icon name="file" size={13} className="tree-scene-icon" />
          {renaming?.id === scene.id ? (
            <RenameInput value={renaming.value} onChange={(v) => setRenaming({ id: scene.id, value: v })} onCommit={commitRename} onCancel={() => setRenaming(null)} />
          ) : (
            <span className="tree-label truncate">{scene.title}</span>
          )}
          <span className="tree-count">{formatNumber(scene.wordCount)}</span>
        </div>
        {status && (
          <div className={`tree-scene-status status-${status.kind}`}>
            <span className="tree-status-dot" /> {status.label}
          </div>
        )}
      </div>
    );
  };

  const renderChapter = (chapter: Chapter) => {
    const scenes = scenesByChapter.get(chapter.id) ?? [];
    const isCollapsed = collapsed.has(chapter.id);
    return (
      <div key={chapter.id} className="tree-chapter-wrap">
        <div
          className={`tree-chapter ${dropHint === `chapter:${chapter.id}` ? "drop-before" : ""}`}
          draggable
          onDragStart={(e) => {
            setDrag({ kind: "chapter", id: chapter.id });
            e.dataTransfer.effectAllowed = "move";
          }}
          onDragOver={(e) => {
            if (drag?.kind === "chapter" || drag?.kind === "scene") {
              e.preventDefault();
              if (drag.kind === "chapter") setDropHint(`chapter:${chapter.id}`);
            }
          }}
          onDragLeave={() => setDropHint((h) => (h === `chapter:${chapter.id}` ? null : h))}
          onDrop={(e) => {
            e.preventDefault();
            setDropHint(null);
            if (drag?.kind === "chapter" && drag.id !== chapter.id) {
              void reorderChaptersForDrop(drag.id, chapter.id);
            } else if (drag?.kind === "scene") {
              // Dropping a scene on the chapter row appends it to that chapter.
              void reorderScenesForDrop(drag.id, drag.chapterId, chapter.id, null);
            }
            setDrag(null);
          }}
          onContextMenu={(e) => openMenu(e, chapterMenu(chapter))}
        >
          <button className="tree-twisty" onClick={() => toggle(chapter.id)} aria-label="Toggle">
            <Icon name={isCollapsed ? "chevronRight" : "chevronDown"} size={13} />
          </button>
          <Icon name="layers" size={13} className="tree-chapter-icon" />
          {renaming?.id === chapter.id ? (
            <RenameInput value={renaming.value} onChange={(v) => setRenaming({ id: chapter.id, value: v })} onCommit={commitRename} onCancel={() => setRenaming(null)} />
          ) : (
            <span className="tree-label truncate" onDoubleClick={() => setRenaming({ id: chapter.id, value: chapter.title })}>
              {chapter.title}
            </span>
          )}
          <span className="tree-count">{formatNumber(chapter.wordCount)}</span>
          <button className="tree-add" title="Add scene" onClick={(e) => { e.stopPropagation(); addScene(chapter.id); }}>
            <Icon name="plus" size={13} />
          </button>
        </div>
        {!isCollapsed && (
          <div className="tree-scenes">
            {scenes.map(renderScene)}
            {scenes.length === 0 ? (
              <button className="tree-empty-add" onClick={() => addScene(chapter.id)}>
                <Icon name="plus" size={12} /> Add first scene
              </button>
            ) : (
              <button className="tree-add-scene" title="New scene" onClick={() => addScene(chapter.id)}>
                <Icon name="plus" size={12} /> <span className="tree-add-label">New Scene</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const partlessChapters = chaptersByPart.get(null) ?? [];

  const allChapterIds = manuscript.chapters.map((c) => c.id);
  const allChaptersCollapsed = allChapterIds.length > 0 && allChapterIds.every((id) => collapsed.has(id));
  const toggleAllChapters = () =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (allChaptersCollapsed) for (const id of allChapterIds) next.delete(id);
      else for (const id of allChapterIds) next.add(id);
      return next;
    });

  return (
    <div className="tree">
      <div className="tree-toolbar">
        <span className="tree-toolbar-title">Manuscript</span>
        <div className="row gap-1">
          <button
            className="btn btn-icon btn-ghost btn-sm"
            title={allChaptersCollapsed ? "Expand all chapters" : "Collapse all chapters"}
            onClick={toggleAllChapters}
            disabled={allChapterIds.length === 0}
          >
            <Icon name={allChaptersCollapsed ? "chevronDown" : "chevronRight"} size={15} />
          </button>
          <button className="btn btn-icon btn-ghost btn-sm" title="Add part" onClick={addPart}>
            <Icon name="book" size={14} />
          </button>
          <button className="btn btn-icon btn-ghost btn-sm" title="Add chapter" onClick={() => addChapter(null)}>
            <Icon name="plus" size={15} />
          </button>
        </div>
      </div>

      <div className="tree-body">
        {manuscript.parts.map((part) => {
          const chapters = chaptersByPart.get(part.id) ?? [];
          const isCollapsed = collapsed.has(part.id);
          return (
            <div key={part.id} className="tree-part">
              <div className="tree-part-head" onContextMenu={(e) => openMenu(e, partMenu(part))}>
                <button className="tree-twisty" onClick={() => toggle(part.id)} aria-label="Toggle">
                  <Icon name={isCollapsed ? "chevronRight" : "chevronDown"} size={13} />
                </button>
                {renaming?.id === part.id ? (
                  <RenameInput value={renaming.value} onChange={(v) => setRenaming({ id: part.id, value: v })} onCommit={commitRename} onCancel={() => setRenaming(null)} />
                ) : (
                  <span className="tree-part-label truncate" onDoubleClick={() => setRenaming({ id: part.id, value: part.title })}>
                    {part.title}
                  </span>
                )}
                <button className="tree-add" title="Add chapter" onClick={(e) => { e.stopPropagation(); addChapter(part.id); }}>
                  <Icon name="plus" size={13} />
                </button>
              </div>
              {!isCollapsed && <div className="tree-part-body">{chapters.map(renderChapter)}</div>}
            </div>
          );
        })}

        {partlessChapters.map(renderChapter)}

        {manuscript.chapters.length > 0 && (
          <button className="tree-add-chapter" title="New chapter" onClick={() => addChapter(null)}>
            <Icon name="plus" size={14} /> <span className="tree-add-label">New Chapter</span>
          </button>
        )}

        {manuscript.chapters.length === 0 && (
          <div className="tree-empty">
            <p className="muted">No chapters yet.</p>
            <button className="btn btn-sm" onClick={() => addChapter(null)}>
              <Icon name="plus" size={14} /> Add chapter
            </button>
          </div>
        )}
      </div>
      {menu}
    </div>
  );
}

/** Map a scene's free-text status to a small labelled indicator for the tree. */
function sceneStatus(raw: string | null): { kind: string; label: string } | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  const l = s.toLowerCase();
  if (l.includes("continu") || l.includes("issue") || l.includes("conflict")) return { kind: "issue", label: "Continuity issue" };
  if (l.includes("ai") || l.includes("suggest")) return { kind: "ai", label: "AI Suggestions" };
  if (l.includes("revis") || l.includes("final") || l.includes("done") || l.includes("complete")) return { kind: "revised", label: s };
  if (l.includes("draft")) return { kind: "draft", label: s };
  return { kind: "draft", label: s };
}

function RenameInput({
  value,
  onChange,
  onCommit,
  onCancel
}: {
  value: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  onCancel: () => void;
}) {
  return (
    <input
      className="tree-rename"
      value={value}
      autoFocus
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit();
        if (e.key === "Escape") onCancel();
      }}
    />
  );
}
